/**
 * useOAuthProviders フック
 *
 * GET /api/auth/oauth/providers から有効な OAuth プロバイダー一覧を取得する。
 * - API 成功時: available=true のプロバイダー ID のみを返す（0 件なら空配列）
 * - API 失敗時（isError）のみ静的フォールバック ['google','microsoft'] を返す（耐障害挙動）
 * - 未設定（available=false のみ）の場合は空配列を返しボタンを非表示にする
 * - TanStack Query で 5 分キャッシュ
 */
import { useQuery } from '@tanstack/react-query';
import { oauthService } from '../services/oauthService';
import type { OAuthProvider, OAuthProviderInfo } from '../types';

const FALLBACK_PROVIDERS: OAuthProvider[] = ['google', 'microsoft'];

export const oauthProvidersQueryKey = ['oauth', 'providers'] as const;

export interface UseOAuthProvidersResult {
  providers: OAuthProvider[];
  isLoading: boolean;
}

export function useOAuthProviders(): UseOAuthProvidersResult {
  const query = useQuery<OAuthProviderInfo[], Error>({
    queryKey: oauthProvidersQueryKey,
    queryFn: () => oauthService.getAvailableProviders(),
    staleTime: 5 * 60 * 1000,
  });

  // API 失敗時（isError）のみフォールバックを使用する。
  // API 成功時は available=true のプロバイダーのみを返し、0 件なら空配列を返す。
  // これにより「未設定でも全プロバイダーボタンが表示される」バグを防ぐ。
  if (query.isError) {
    return { providers: FALLBACK_PROVIDERS, isLoading: false };
  }

  const providers = (query.data ?? [])
    .filter((p) => p.available)
    .map((p) => p.id);

  return { providers, isLoading: query.isLoading };
}
